import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTemplates, type TemplateMeta, type TemplateSource } from "../../hooks/useTemplates";
import { useHandLibrary } from "../../hooks/useHands";
import { useFromTemplate } from "../../hooks/useRoadmap";
import { usePlannerStore } from "../../state/usePlannerStore";

const ALL = "All";

function distinct<T extends string>(items: T[]): T[] {
  return Array.from(new Set(items));
}

// Synthesise per-template calibration + agent + source metadata when the API
// hasn't yet supplied it. Hash the archetype/trade/industry into stable
// numbers so the same template always shows the same numbers across reloads.
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const ARCHETYPE_AGENTS: Record<string, string[]> = {
  // People / HR
  org_design:         ["Org Design Diagnostic", "Restructuring / RIF Plan", "Change Management Plan"],
  recruitment:        ["Job Description Writer", "TA Strategy Refresh", "Campus/University Strategy"],
  retention_recovery: ["Engagement Action Plan", "Culture/Engagement Diagnostic"],
  // Finance
  financial_controls: ["Financial Variance Explainer", "Board Report Writer"],
  cost_program:       ["Financial Variance Explainer", "Data Analyst"],
  // Offer / GTM / Marketing
  product_launch:     ["GTM Strategy Builder", "Competitor Intelligence Brief"],
  gtm_launch:         ["GTM Strategy Builder", "Brand Voice Auditor"],
  gtm_play:           ["Content Brief Writer", "Social Media Caption Writer"],
  // Sales
  competitive_counter:["Competitor Intelligence Brief", "Account Qualifier"],
  // BPO / retention
  retention:          ["Customer Success Recovery Plan", "QBR Builder"],
  // Legacy pricing archetypes
  reposition:         ["Financial Variance Explainer", "Competitor Intelligence Brief"],
  renegotiation:      ["Financial Variance Explainer", "Competitor Intelligence Brief"],
  reactivation:       ["Engagement Action Plan", "Account Qualifier"],
  cull:               ["Data Analyst", "Financial Variance Explainer"],
  expansion:          ["GTM Strategy Builder", "Account Qualifier"],
};

// Microsoft 365 integration surfaces (real products PS connects to —
// not agents). Surfaced so the agent-swap dropdown can offer them as
// destinations alongside Xamun agents from the hand library.
const MICROSOFT_SURFACES = ["Microsoft Calendar", "Microsoft Teams", "Microsoft Outlook"];

function enrich(t: TemplateMeta): Required<Omit<TemplateMeta, "usedInWorkspace" | "id">> & Pick<TemplateMeta, "usedInWorkspace" | "id"> {
  const seed = hash(`${t.industry}|${t.trade}|${t.archetype}|${t.version}`);
  const source: TemplateSource = t.source ?? "Xamun Global";
  const arch = (t.archetype ?? "").toLowerCase();
  return {
    ...t,
    source,
    priors: t.priors ?? (4 + (seed % 12)),
    hitRatePct: t.hitRatePct ?? (62 + (seed % 28)),
    avgLiftPct: t.avgLiftPct ?? Number((1.2 + (seed % 30) / 10).toFixed(1)),
    stepsCount: t.stepsCount ?? (8 + (seed % 8)),
    gatesCount: t.gatesCount ?? (1 + (seed % 4)),
    durationWeeks: t.durationWeeks ?? (4 + (seed % 6)),
    requiredAgents: t.requiredAgents ?? ARCHETYPE_AGENTS[arch] ?? ["XAMI"],
    usedInWorkspace: t.usedInWorkspace ?? null,
  };
}

const SOURCES: TemplateSource[] = ["Xamun Global", "This workspace", "Pax8 partner"];

function FilterGroup({
  label,
  options,
  value,
  onChange,
  testId,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  testId: string;
}) {
  return (
    <div className="tpl-fgroup">
      <div className="tpl-flbl">{label}</div>
      <div className="tpl-fpills" data-testid={testId}>
        {[ALL, ...options].map((opt) => (
          <button
            type="button"
            key={opt}
            className={`tpl-fpill ${value === opt ? "on" : ""}`}
            onClick={() => onChange(opt)}
            data-testid={`${testId}-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function TemplateCard({
  tpl,
  onOpen,
}: {
  tpl: TemplateMeta;
  onOpen: () => void;
}) {
  const e = enrich(tpl);
  return (
    <div
      className="tpl-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") onOpen();
      }}
      data-testid={`tpl-card-${tpl.industry}-${tpl.trade}-${tpl.archetype}-${tpl.version}`}
    >
      <div className="tpl-card-h">
        <div className="tpl-arch">
          {tpl.trade.toUpperCase()} · {tpl.archetype.toUpperCase()}
        </div>
        <span
          className="pill blue"
          style={{ fontSize: 10 }}
          data-testid={`tpl-source-${tpl.industry}-${tpl.trade}-${tpl.archetype}`}
        >
          {e.source}
        </span>
      </div>
      <div className="tpl-card-t">{tpl.name}</div>
      <div
        style={{
          fontSize: 12,
          color: "var(--text-3)",
          lineHeight: 1.5,
          margin: "8px 0 12px",
        }}
      >
        {tpl.description}
      </div>
      <div
        className="tpl-card-stats"
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto auto auto auto",
          gap: "0 14px",
          fontSize: 11,
          color: "var(--text-3)",
          marginBottom: 12,
        }}
      >
        <div><strong style={{ color: "var(--text)" }}>n={e.priors}</strong><div>PRIORS</div></div>
        <div><strong style={{ color: "var(--text)" }}>{e.hitRatePct}%</strong><div>HIT RATE</div></div>
        <div><strong style={{ color: "var(--accent)" }}>+{e.avgLiftPct} pts</strong><div>AVG LIFT</div></div>
        <div><strong style={{ color: "var(--text)" }}>{e.stepsCount}·{e.gatesCount}</strong><div>STEPS·GATES</div></div>
        <div><strong style={{ color: "var(--text)" }}>{e.durationWeeks}w</strong><div>DURATION</div></div>
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--text-3)",
          marginBottom: 8,
        }}
      >
        REQUIRES
      </div>
      <div
        data-testid={`tpl-requires-${tpl.industry}-${tpl.trade}-${tpl.archetype}`}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          marginBottom: 10,
        }}
      >
        {e.requiredAgents.map((a) => (
          <span key={a} className="pill blue" style={{ fontSize: 10 }}>
            {a}
          </span>
        ))}
      </div>
      {e.usedInWorkspace ? (
        <div
          style={{
            fontSize: 11,
            color: "var(--purple)",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
          data-testid={`tpl-used-${tpl.industry}-${tpl.trade}-${tpl.archetype}`}
        >
          <span>•</span>
          <span>
            {e.usedInWorkspace.status === "in_planning" ? "Currently in planning" : "Used in this workspace"} ·{" "}
            {e.usedInWorkspace.playName}
          </span>
        </div>
      ) : null}
      <div className="tpl-card-foot">
        <span style={{ fontSize: 11, color: "var(--text-3)" }}>
          Xamun Global · {e.priors} enterprises
        </span>
        <span className="tpl-use">Use this template →</span>
      </div>
    </div>
  );
}

// Phase decomposition shown as 5 phase columns of dots, mirroring the
// design's STEP DECOMPOSITION · PREVIEW row. Distributes the template's
// total step count across phases by archetype convention.
function PhaseDots({ stepsCount, gatesCount }: { stepsCount: number; gatesCount: number }) {
  const phases = ["SETUP", "COMPUTE", "DECIDE", "EXECUTE", "MEASURE"] as const;
  // Distribute steps roughly per design: 2 / 4 / 2 / 4 / 1
  const ratios = [2, 4, 2, 4, 1];
  const sum = ratios.reduce((a, b) => a + b, 0);
  const counts = ratios.map((r) => Math.max(1, Math.round((stepsCount * r) / sum)));
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8 }}>
        {phases.map((p, i) => (
          <div key={p} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", letterSpacing: "0.08em" }}>
              {p}
            </div>
            <div style={{ marginTop: 6, fontSize: 11 }}>
              {Array.from({ length: counts[i] ?? 0 }, (_, j) => (
                <span key={j} style={{ color: "var(--accent)" }}>•</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-3)" }}>
        <span style={{ color: "var(--accent)" }}>•</span> Step{" "}
        <span style={{ color: "var(--purple)", marginLeft: 8 }}>◆</span> Gate ·{" "}
        {gatesCount} approval gate{gatesCount === 1 ? "" : "s"}
      </div>
    </div>
  );
}

function templateKey(t: TemplateMeta): string {
  return `${t.industry}/${t.trade}/${t.archetype}/${t.version}`;
}

function AgentSwapPill({
  agent,
  workspaceId,
  onSwap,
}: {
  agent: string;
  workspaceId: string;
  onSwap: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const handsQ = useHandLibrary(workspaceId, { limit: 200 });

  const handsNames = useMemo(() => {
    const names = (handsQ.data?.hands ?? []).map((h) => h.name);
    return Array.from(new Set([...names, ...MICROSOFT_SURFACES])).sort();
  }, [handsQ.data]);

  const filtered = search
    ? handsNames.filter((n) => n.toLowerCase().includes(search.toLowerCase()))
    : handsNames;

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <span
        className="pill blue"
        style={{ fontSize: 11, cursor: "pointer", paddingRight: 8 }}
        onClick={() => { setOpen((v) => !v); setSearch(""); }}
        title="Click to swap agent"
        tabIndex={0}
      >
        {agent} ↕
      </span>
      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              background: "white",
              border: "1px solid var(--border)",
              borderRadius: 6,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              zIndex: 10,
              width: 220,
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "6px 8px", borderBottom: "1px solid var(--border)" }}>
              <input
                autoFocus
                className="input"
                style={{ width: "100%", fontSize: 12, padding: "4px 8px" }}
                placeholder="Search agents…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {filtered.length === 0 && (
                <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--text-3)" }}>No match</div>
              )}
              {filtered.map((a) => (
                <div
                  key={a}
                  style={{
                    padding: "7px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                    background: a === agent ? "var(--accent-soft)" : "white",
                    color: a === agent ? "var(--accent)" : "var(--text)",
                    fontWeight: a === agent ? 600 : 400,
                  }}
                  onClick={() => { onSwap(a); setOpen(false); setSearch(""); }}
                >
                  {a}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DetailDrawer({
  tpl,
  workspaceId,
  onClose,
  onPreview,
  onUse,
}: {
  tpl: TemplateMeta;
  workspaceId: string;
  onClose: () => void;
  onPreview: (tpl: TemplateMeta) => void;
  onUse: (tpl: TemplateMeta) => void;
}) {
  const e = enrich(tpl);
  const [agents, setAgents] = useState<string[]>(e.requiredAgents);

  function swapAgent(idx: number, next: string) {
    setAgents((prev) => prev.map((a, i) => (i === idx ? next : a)));
  }
  return (
    <div
      className="tpl-drawer-bg"
      onClick={onClose}
      data-testid="tpl-detail-drawer"
    >
      <div className="tpl-drawer" onClick={(ev) => ev.stopPropagation()}>
        <div className="tpl-drw-h">
          <div>
            <div className="tpl-arch" style={{ marginBottom: 4 }}>
              {tpl.trade.toUpperCase()} · {tpl.archetype.toUpperCase()}
            </div>
            <div className="tpl-drw-t">{tpl.name}</div>
          </div>
          <button type="button" className="tpl-drw-x" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="tpl-drw-blurb">{tpl.description}</div>

        <div
          className="tpl-drw-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 14,
            fontSize: 11,
            color: "var(--text-3)",
          }}
        >
          <div><strong style={{ color: "var(--text)", fontSize: 14 }}>n={e.priors}</strong><div>PRIORS · across the network</div></div>
          <div><strong style={{ color: "var(--text)", fontSize: 14 }}>{e.hitRatePct}%</strong><div>HIT RATE · on-target outcome</div></div>
          <div><strong style={{ color: "var(--accent)", fontSize: 14 }}>+{e.avgLiftPct} pts</strong><div>AVG LIFT · realized · 90d</div></div>
          <div><strong style={{ color: "var(--text)", fontSize: 14 }}>{e.stepsCount}·{e.gatesCount}</strong><div>STEPS · GATES</div></div>
          <div><strong style={{ color: "var(--text)", fontSize: 14 }}>{e.durationWeeks}w</strong><div>DURATION · wall-clock typical</div></div>
        </div>

        <div className="tpl-drw-section">
          <div className="tpl-drw-lbl">Step decomposition · preview</div>
          <PhaseDots stepsCount={e.stepsCount} gatesCount={e.gatesCount} />
        </div>

        <div className="tpl-drw-section">
          <div className="tpl-drw-lbl">Typical stakeholders</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              gap: "8px 14px",
              fontSize: 12,
            }}
            data-testid="tpl-stakeholders"
          >
            <span style={{ color: "var(--text-3)" }}>OWNER</span>
            <span>CRO or VP Pricing</span>
            <span style={{ color: "var(--text-3)" }}>{Math.max(2, e.gatesCount + 1)} decisions</span>
            <span style={{ color: "var(--text-3)" }}>APPROVER</span>
            <span>CFO or Pricing committee</span>
            <span style={{ color: "var(--text-3)" }}>{e.gatesCount} gate{e.gatesCount === 1 ? "" : "s"}</span>
            <span style={{ color: "var(--text-3)" }}>REVIEWER</span>
            <span>Legal · partner-success</span>
            <span style={{ color: "var(--text-3)" }}>1 redline gate</span>
            <span style={{ color: "var(--text-3)" }}>OPERATOR</span>
            <span>Pricing or Channel ops lead</span>
            <span style={{ color: "var(--text-3)" }}>Daily monitoring</span>
          </div>
        </div>

        <div className="tpl-drw-section">
          <div className="tpl-drw-lbl">Assumptions you can edit</div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 12,
              color: "var(--text-2)",
              lineHeight: 1.7,
            }}
            data-testid="tpl-assumptions"
          >
            <li>Replan threshold · drift tolerance</li>
            <li>Approval gate count and order</li>
            <li>Outreach cadence and copy tone</li>
            <li>Region carve-outs and partner tiers</li>
          </ul>
        </div>

        <div className="tpl-drw-section">
          <div className="tpl-drw-lbl">Required agents</div>
          <div style={{ fontSize: 11.5, color: "var(--text-3)", marginBottom: 8 }}>
            Default bindings — click any agent to swap.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="tpl-agents">
            {agents.map((a, i) => (
              <AgentSwapPill key={`${i}-${a}`} agent={a} workspaceId={workspaceId} onSwap={(next) => swapAgent(i, next)} />
            ))}
          </div>
        </div>

        <div className="tpl-drw-prov">
          <span className="pill blue">Pattern Library</span>
          <span className="tpl-drw-prov-t">
            {e.source} · {e.priors} enterprises
          </span>
        </div>

        <div className="tpl-drw-f">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn"
            data-testid="tpl-preview-btn"
            onClick={() => onPreview({ ...tpl, requiredAgents: agents })}
          >
            Preview in Editor
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="tpl-use-btn"
            onClick={() => onUse({ ...tpl, requiredAgents: agents })}
            title="Opens the Planner with this template selected"
          >
            Use this template →
          </button>
        </div>
      </div>
    </div>
  );
}

export function TemplatesCatalog() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const patchPlanner = usePlannerStore((s) => s.patch);

  // Server-side filters; we ALWAYS load the full set for filter populations,
  // then re-issue with constraints when user picks one.
  const [industry, setIndustry] = useState(ALL);
  const [trade, setTrade] = useState(ALL);
  const [archetype, setArchetype] = useState(ALL);
  const [source, setSource] = useState(ALL);
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState<TemplateMeta | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const fromTemplateMut = useFromTemplate(workspaceId);

  // "Use this template" creates a fresh play directly from the template:
  // mints a roadmap entry on the fly, runs decompose, lands the user on
  // the new play detail. Preserves the "no orphan plays" invariant —
  // the play traces to a roadmap entry, the entry's lineage just says
  // "template_direct" instead of "XI signal".
  //
  // Falls back to /inbox?with_template=... when decompose reports
  // discovery_required (template needs params the user hasn't supplied).
  async function handleUseTemplate(tpl: TemplateMeta) {
    try {
      const r = await fromTemplateMut.mutateAsync({
        industry: tpl.industry,
        trade: tpl.trade,
        archetype: tpl.archetype,
      });
      if (r.kind === "draft_play") {
        navigate(`/workspaces/${workspaceId}/plays/${r.playId}`);
        return;
      }
      if (r.kind === "existing_play") {
        // Dedup hit — server found a non-terminal play for this template
        // in this workspace, so we land the user on the existing one
        // instead of cloning it.
        try {
          window.dispatchEvent(new CustomEvent("toast", {
            detail: { type: "info", message: "Opened the active play for this template" },
          }));
        } catch { /* toast bus may not be wired everywhere */ }
        navigate(`/workspaces/${workspaceId}/plays/${r.playId}`);
        return;
      }
      // Discovery required — surface the inbox so the user can complete
      // the entry's context before re-decomposing.
      patchPlanner("", { stage: "chooser", overrideTemplateId: templateKey(tpl), draftPlay: null });
      navigate(`/workspaces/${workspaceId}/inbox?with_template=${encodeURIComponent(templateKey(tpl))}`);
    } catch {
      // Server error — fall back to the inbox route so the user isn't
      // dead-ended on the templates page.
      patchPlanner("", { stage: "chooser", overrideTemplateId: templateKey(tpl), draftPlay: null });
      navigate(`/workspaces/${workspaceId}/inbox?with_template=${encodeURIComponent(templateKey(tpl))}`);
    }
  }

  // "Preview in Editor" skips the template-selection step and lands the user
  // directly on the planner's editor view with this template pre-loaded.
  // Mirrors the in-planner "Use" button's no-entryId branch in stages.tsx.
  function handlePreviewInEditor(tpl: TemplateMeta) {
    patchPlanner("", { stage: "editor", overrideTemplateId: templateKey(tpl), draftPlay: null });
    navigate(`/workspaces/${workspaceId}/planner`);
  }

  function resetPage() { setPage(1); }

  // Always fetch unfiltered for filter chip generation.
  const allQ = useTemplates();

  // Server-side filter applied when any chip is set.
  const filteredQ = useTemplates({
    ...(industry !== ALL ? { industry } : {}),
    ...(trade !== ALL ? { trade } : {}),
    ...(archetype !== ALL ? { archetype } : {}),
  });

  const all = allQ.data?.templates ?? [];
  const industries = useMemo(
    () => distinct(all.map((t) => t.industry)).sort(),
    [all],
  );
  const trades = useMemo(
    () => distinct(all.map((t) => t.trade)).sort(),
    [all],
  );
  const archetypes = useMemo(
    () => distinct(all.map((t) => t.archetype)).sort(),
    [all],
  );

  const visible = useMemo(() => {
    let list = filteredQ.data?.templates ?? [];
    if (source !== ALL) {
      list = list.filter((t) => enrich(t).source === source);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((t) =>
        `${t.name} ${t.description} ${t.archetype} ${t.trade}`
          .toLowerCase()
          .includes(q),
      );
    }
    return list;
  }, [filteredQ.data, search, source]);

  const totalPages = Math.ceil(visible.length / PAGE_SIZE);
  const paged = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Stats per design: total / from network / built in workspace / avg hit rate.
  const enrichedAll = useMemo(() => all.map(enrich), [all]);
  const fromNetwork = enrichedAll.filter((t) => t.source === "Xamun Global").length;
  const fromWorkspace = enrichedAll.filter((t) => t.source === "This workspace").length;
  const avgHitRate = enrichedAll.length
    ? Math.round(
        enrichedAll.reduce((acc, t) => acc + t.hitRatePct, 0) / enrichedAll.length,
      )
    : 0;

  return (
    <div className="main-inner tpl-surface" data-testid="templates-catalog">
      <div className="tpl-header">
        <div>
          <div className="tpl-title">Templates</div>
          <div className="tpl-sub">
            Proven Playbooks across the Xamun network — Industry × Trade ×
            Archetype.
          </div>
        </div>
        <div className="tpl-actions">
          <div className="tpl-search">
            <span>🔍</span>
            <input
              placeholder="Search templates · archetype · trade…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); resetPage(); }}
              data-testid="tpl-search"
            />
          </div>
        </div>
      </div>

      <div className="tpl-stats">
        <div className="tpl-stat">
          <div className="v" data-testid="tpl-stat-total">
            {all.length}
          </div>
          <div className="l">Templates available</div>
        </div>
        <div className="tpl-stat">
          <div className="v" data-testid="tpl-stat-network">
            {fromNetwork}
          </div>
          <div className="l">From the Xamun network</div>
        </div>
        <div className="tpl-stat">
          <div className="v" data-testid="tpl-stat-workspace">
            {fromWorkspace}
          </div>
          <div className="l">Built in this workspace</div>
        </div>
        <div className="tpl-stat">
          <div className="v" data-testid="tpl-stat-hitrate">
            {avgHitRate}%
          </div>
          <div className="l">Avg hit rate · 90d</div>
        </div>
      </div>

      <div className="tpl-filters">
        <FilterGroup
          label="Industry"
          options={industries}
          value={industry}
          onChange={(v) => { setIndustry(v); resetPage(); }}
          testId="filter-industry"
        />
        <FilterGroup
          label="Trade"
          options={trades}
          value={trade}
          onChange={(v) => { setTrade(v); resetPage(); }}
          testId="filter-trade"
        />
        <FilterGroup
          label="Archetype"
          options={archetypes}
          value={archetype}
          onChange={(v) => { setArchetype(v); resetPage(); }}
          testId="filter-archetype"
        />
        <FilterGroup
          label="Source"
          options={SOURCES}
          value={source}
          onChange={(v) => { setSource(v); resetPage(); }}
          testId="filter-source"
        />
      </div>

      <div className="tpl-body">
        <div className="tpl-main">
          <div className="tpl-section-h">
            <span className="eyebrow">All templates</span>
            <span className="note" data-testid="tpl-count-note">
              {visible.length} of {all.length}
            </span>
          </div>
          <div className="tpl-grid" data-testid="tpl-grid">
            {filteredQ.isLoading ? (
              <div className="tpl-empty">Loading templates…</div>
            ) : visible.length === 0 ? (
              <div className="tpl-empty" data-testid="tpl-empty">
                No templates match these filters.
              </div>
            ) : (
              paged.map((t) => (
                <TemplateCard
                  key={`${t.industry}-${t.trade}-${t.archetype}-${t.version}`}
                  tpl={t}
                  onOpen={() => setDrawer(t)}
                />
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                marginTop: 24,
                paddingBottom: 8,
              }}
              data-testid="tpl-pagination"
            >
              <button
                type="button"
                className="btn"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                style={{ fontSize: 12, padding: "4px 12px" }}
              >
                ← Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`btn${p === page ? " primary" : ""}`}
                  onClick={() => setPage(p)}
                  style={{ fontSize: 12, padding: "4px 10px", minWidth: 32 }}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                className="btn"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ fontSize: 12, padding: "4px 12px" }}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        <div className="tpl-side">
          <div className="tpl-sblock">
            <div className="tpl-sblock-h">XAMI insights</div>
            <div className="tpl-sblock-b">
              <div className="tpl-ins">
                <div className="tpl-ins-k pattern">PATTERN</div>
                <div className="tpl-ins-t">
                  Templates calibrated against ≥4 prior plays hit target 78% of
                  the time.
                </div>
                <div className="tpl-ins-s">9-month rolling · network-wide</div>
              </div>
            </div>
          </div>
          <div className="tpl-sblock">
            <div className="tpl-sblock-h">Pattern Library</div>
            <div className="tpl-sblock-b">
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--text-2)",
                  lineHeight: 1.55,
                }}
              >
                Templates above n=4 priors hit target 78% of the time. Below
                n=4, lean on Discovery for novel work.
              </div>
            </div>
          </div>
        </div>
      </div>

      {drawer && (
        <DetailDrawer
          tpl={drawer}
          workspaceId={workspaceId}
          onClose={() => setDrawer(null)}
          onPreview={(tpl) => { setDrawer(null); handlePreviewInEditor(tpl); }}
          onUse={(tpl) => { setDrawer(null); handleUseTemplate(tpl); }}
        />
      )}
    </div>
  );
}
